// src/services/pushNotificationService.js
import { pushNotificationService as supabasePushService } from '../lib/supabase';

class PushNotificationService {
  constructor() {
    this.isSupported = this.checkSupport();
    this.vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  }

  checkSupport() {
    return (
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window
    );
  }

  async requestPermission(email = null) {
    if (!this.isSupported) {
      return { success: false, error: 'not_supported' };
    }

    try {
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        const subscription = await this.subscribeToPush();
        
        if (subscription) {
          // Save to Supabase
          await supabasePushService.subscribe({
            email,
            endpoint: subscription.endpoint,
            keys: subscription.toJSON().keys,
            browser: this.getBrowserInfo(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: navigator.language,
            userAgent: navigator.userAgent
          });
          
          return { success: true, subscription, permission: 'granted' };
        }
      } else if (permission === 'denied') {
        return { success: false, error: 'permission_denied', permission: 'denied' };
      } else {
        return { success: false, error: 'permission_dismissed', permission: 'default' };
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      return { success: false, error: error.message };
    }
  }

  async subscribeToPush() {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        const applicationServerKey = this.urlBase64ToUint8Array(this.vapidPublicKey);

        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey
        });
      }

      return subscription;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      return null;
    }
  }

  async isSubscribed() {
    if (!this.isSupported) return false;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Check with Supabase
        return await supabasePushService.isSubscribed(subscription.endpoint);
      }
      
      return false;
    } catch (error) {
      console.error('Error checking subscription:', error);
      return false;
    }
  }

  async unsubscribe() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await supabasePushService.unsubscribe(subscription.endpoint);
        return true;
      }
    } catch (error) {
      console.error('Error unsubscribing:', error);
      return false;
    }
  }

  getPermissionStatus() {
    if (!this.isSupported) return 'unsupported';
    return Notification.permission;
  }

  getBrowserInfo() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  async sendTestNotification() {
    if (Notification.permission === 'granted') {
      const registration = await navigator.serviceWorker.ready;
      registration.showNotification('ConnectCo Daily Prompt ☕', {
        body: 'What\'s your favorite Tokyo coffee spot this week?',
        icon: '/connectco-icon.png',
        badge: '/connectco-badge.png',
        tag: 'daily-prompt-test',
        data: { url: '/' }
      });
    }
  }
}

export default new PushNotificationService();